import { createSlice } from '@reduxjs/toolkit'
import * as Sentry from '@sentry/react'
import { endOfDay, formatISO, isAfter, isBefore, min, parseISO, subDays } from 'date-fns'
import { mapValues, pickBy } from 'lodash-es'
import { useSelector } from 'react-redux'
import { checkJWT, endOfToday, isToday, JWTExpired, PumpkinTimer, useObjSelector } from 'utils'
import { newDate } from 'utils/date'
import FMT from 'utils/fmt'
import HTTP_CODE from 'utils/HTTP_CODE'
import API from './API'
import Order from './models/Order'
import TabletOptions from './models/TabletOptions'
import { getActiveRestaurant } from './Restaurants'

const initialState = {
	current: {},
	dates  : {
		start        : FMT.dateISO(newDate(null)),
		end          : FMT.dateISO(newDate(null)),
		pumpkinTime  : null,
		pumpkinStreak: 0,
	},
	list        : [],
	search      : [],
	updatedAt   : '', //this is only for the lists!
	ccPwdTimeout: null,

	//TODO this would probably be better inside the current order? Right now, it gets cleaned when loading another "current order"
	currentDeliveryOptions: null, //FIXME double check if this doesn't require any migration

	//TODO: in case we need more organization with the orders list, see createEntityAdapter(): https://github.com/reduxjs/redux-toolkit/blob/master/docs/usage/usage-guide.md#normalizing-with-createentityadapter
}

const slice = createSlice({
	name: 'orders',
	initialState,

	reducers: {
		load       : (state, { payload: orders }) => {
			state.list      = typeof orders == 'object'? orders : state.list //just in case we get some debug trash
			state.updatedAt = formatISO(newDate(null))
		},
		appendOrder: (state, { payload: order }) => {
			if (!hasOrder(order.id)({ orders: state })) {
				state.list.push(order)
			}
		},

		clearOrders: state => initialState,

		/**
		 * @param state {object}
		 * @param payload {Date}
		 */
		setPumpkinTime  : (state, { payload }) => {
			state.dates.pumpkinTime = payload? FMT.dateISO(payload) : null
		},
		setPumpkinStreak: (state, { payload }) => {
			state.dates.pumpkinStreak = payload
		},
		setDates        : (state, { payload: { start, end } }) => {
			state.dates.start = start || state.dates.start
			state.dates.end   = end || state.dates.end
		},

		setSearch  : (state, { payload: orders }) => { state.search = orders },
		clearSearch: state => { state.search = [] },

		setCurrentOrder   : (state, { payload: order }) => {
			state.current = order
			state.currentDeliveryOptions = null
		},
		updateCurrentOrder: (state, { payload }) => {
			const order = new Order(state.current)

			Object.assign(state.current, payload, { modified_at: order.newDate().toISOString() })

			const orderKey = state.list.findIndex(order => order.id == state.current.id)
			if (orderKey >= 0) { //might not be in the list if coming from a direct link or the Future list
				Object.assign(state.list[orderKey], payload)
			}
		},
		updateOrderFromList: (state, { payload }) => {
			// Object.assign(state.list.findIndex(order => order.customer_id === payload.customer_id), payload)
			state.list.forEach(order => {
				if (order.customer_id === payload.customer_id) {
					Object.assign(order, payload)
				}
			})
		},

		setCcPwdTimeout: (state, { payload: date }) => { state.ccPwdTimeout = date },

		loadDeliveryOptions: (state, { payload: thirdParties }) => {
			state.currentDeliveryOptions = thirdParties
		},
	},
})

//region thunks - not exactly official, but enough for some organization
slice.thunks = {
	fetchOrders: async (dispatch, getState) => {
		const state = getState()
		const resp  = await API.get('order', {
			params: {
				restaurant: getActiveRestaurant(state)?.id,
				start     : FMT.dateISO(min([newDate(null), parseISO(state.orders.dates.start)])),
				end       : state.orders.dates.end,
				future    : 1, //we filter by current/future at getOrders()
			},
		})
		dispatch(slice.actions.load(resp.data))
	},

	/**
	 * Stores the given dates and updates the list of orders.
	 * @param start {?string} Y-m-d
	 * @param end {?string} Y-m-d
	 * @param isPumpkin {boolean} Is this time change user-made or automatic?
	 * @return {function}
	 */
	updateDates: (start, end, isPumpkin = false) => async (dispatch, getState) => {
		const dates      = getDates(getState())
		const endChanged = (dates.end != end)
		if ((start && dates.start != start) || (end && endChanged)) {
			dispatch(slice.actions.setDates({ start, end }))
			dispatch(slice.thunks.fetchOrders)
		}

		if (endChanged) {
			console.log('End date changed; let\'s check if we need to tell the godmother...')
			isToday(parseISO(end))?
				PumpkinTimer.schedule() :
				PumpkinTimer.unschedule()
		}

		//used to advance the start date once there's quite some time the Pumpkin Timer has been the sole source of dates
		const state = getState()
		dispatch(slice.actions.setPumpkinStreak(
			!isPumpkin? 0 : (state.orders.dates.pumpkinStreak || 0) + 1
		))

	},

	searchApiOrders: (query, period) => async dispatch => {
		const NOW = newDate(null)
		dispatch(slice.thunks.customOrdersSearch({
			start : FMT.dateISO(subDays(NOW, period)),
			end   : FMT.dateISO(NOW),
			q     : query,
			future: 1 //better to be safe, in case they're looking for a future order?
		}))
	},

	/**
	 * Free-form API call for orders.
	 * Check the API docs for all available params and their formatting. You're the sole responsible for getting it all right!
	 * Some tips:
	 * - `start` and `end` are ISO-formatted dates
	 * - `q` is used for custom text searches
	 * - `restaurant` can't be set - it's forced to the selected location, if any
	 * @param {{ q: string|undefined, start: string, end: string }} params
	 * @return {(function(*, *): Q.Promise<void>)|*}
	 */
	customOrdersSearch: params => async (dispatch, getState) => {
		params = { ...params, restaurant: getActiveRestaurant(getState())?.id }
		const resp = await API.get('order', { params })
		dispatch(slice.actions.setSearch(resp.data))
	},

	fetchOrder: id => async dispatch => {
		const resp = await API.get(`order/${id}`)
		dispatch(slice.actions.setCurrentOrder(resp.data))
	},

	toggleCancelOrder: (id, is_cancelled) => async dispatch => {
		const operation = is_cancelled? 'cancel' : 'uncancel'
		await API.patch(`order/${id}/${operation}`)
		dispatch(slice.actions.updateCurrentOrder({ is_cancelled }))
	},

	toggleCompleteOrder: (id, complete) => async dispatch => {
		const operation    = complete? 'complete' : 'uncomplete'
		const is_completed = (await API.patch(`order/${id}/${operation}`)).data
		dispatch(slice.actions.updateCurrentOrder({ is_completed }))
	},

	resendOrder: (id, channels, msg) => async () => await API.post(`order/${id}/resend`, { channels, msg }),

	/**
	 * @throws 402 Wrong password
	 * @throws 410 Payment details already removed
	 * @throws 429 Too many wrong attempts
	 */
	grabPaymentInfo: (id, password = null) => async (dispatch, getState) => {
		if (!password) {
			console.debug('Hmmm, no password and trying to get card info? Let me check if you\'ve been authorized before...')
			let ccPwdTimeout = false
			try {
				ccPwdTimeout = await getCcPwdTimeout(getState())
			} catch (e) {
				if (e instanceof JWTExpired) {
					dispatch(slice.actions.setCcPwdTimeout(null)) //remove it, in case it's there but expired
				}
			}

			if (!ccPwdTimeout) {
				console.debug('...nope! You haven\'t! Shoo!')
				return false
			}
			console.debug('...it seems you have. Welcome back.')

			//listens to when the user leaves the app/page, so the timeout is removed, and they'll need to re-enter the pwd
			//not using addEventListener as it would create infinite listeners multiple, and it would be hard to remove them
			document.onvisibilitychange = () => {
				// console.log('visibility changed')
				if (document.visibilityState == 'hidden') {
					console.log('page visibility: hidden = drop pwd timeout!')
					dispatch(slice.actions.setCcPwdTimeout(null))
				}
			}
		}

		const result = await API.postAndHandle(
			`order/${id}/payment_info`, [
				HTTP_CODE.PAYMENT_REQUIRED__402, //wrong pwd
				HTTP_CODE.GONE__410, //no more details
				HTTP_CODE.TOO_MANY_REQUESTS__429, //too many wrong tries
			],
			{ password }
		)
		if (result.headers['x-auth-until']) {
			dispatch(slice.actions.setCcPwdTimeout(result.headers['x-auth-until']))
		} else {
			console.error('Weird... the X-Auth-Until header is missing!')
			Sentry.captureMessage('The X-Auth-Until header is missing', 'error')
		}
		return result
	},

	toggleVIP: (customer_id, is_customer_vip) => async dispatch => {
		const setVIP = is_customer_vip => {
			dispatch(slice.actions.updateCurrentOrder({ is_customer_vip }))
			dispatch(slice.actions.updateOrderFromList({ customer_id, is_customer_vip }))
		}

		setVIP(is_customer_vip) //optimistic call for a simple UI change
		try {
			const result = await API.patch(`customer/${customer_id}/VIP`)
			if (result.data != is_customer_vip) { //maybe some doubled operation left us out of sync?
				// noinspection ExceptionCaughtLocallyJS
				throw new Error('Upstream VIP status mismatch') //no need to pass the status back since it can only go back to the opposite
			}
		} catch (e) {
			setVIP(!is_customer_vip) //undo in case of failures
			throw e
		}
	},

	/**
	 * @param id {number}
	 * @param medium {'sms'|'email'|'both'}
	 * @param msgId {number}
	 */
	notifyCustomer: (id, medium, msgId) => async () => await API.post(`order/${id}/notify/${medium}/${msgId}`),

	rescheduleOrder: (id, minutes, notify) => async dispatch => {
		const resp = await API.patch(`order/${id}/reschedule/${minutes}`, null, { params: { notify } })
		dispatch(slice.actions.updateCurrentOrder(resp.data))
	},

	adjustOrder: (id, type, item_id, amount, reason, origin) => async dispatch => {
		const resp = await API.patchAndHandle(
			`order/${id}/${type}`,
			{ permanentErrors: true },
			pickBy({ item_id, amount, reason, origin })
		)
		dispatch(slice.actions.updateCurrentOrder(resp.data))
	},

	removeAdjustment: (id, item_id) => async dispatch => {
		const resp = await API.delete(`order/${id}/adjustment/${item_id}`)
		dispatch(slice.actions.updateCurrentOrder(resp.data))
	},

	estimateDelivery: (id) => async dispatch => {
		const resp    = await API.get(`delivery/${id}/estimates`)
		const entries = (resp.data || []).filter(e => !e.error && e.id)
		dispatch(slice.actions.loadDeliveryOptions(entries))
		dispatch(slice.actions.updateCurrentOrder({ deliver_by_ourselves: false }))
	},

	assignDriver: (id, name, estimateId) => async dispatch => {
		const resp = await API.patch(`delivery/${id}/driver`, { name, estimateId })
		dispatch(slice.actions.updateCurrentOrder(resp.data))
	},

	cancelDriver: (id) => async dispatch => {
		const resp = await API.delete(`delivery/${id}/driver`)
		dispatch(slice.actions.updateCurrentOrder(resp.data))
	},

	deleteDelivery: (id) => async dispatch => {
		const resp = await API.delete(`delivery/${id}/`)
		dispatch(slice.actions.updateCurrentOrder(resp.data))
	},
}
//endregion

//region State selectors
export const getUpdatedAt = ({ orders }) => orders.updatedAt? parseISO(orders.updatedAt) : null

/**
 * Use with {@link useObjSelector()}
 * @param state
 * @return {{start,end}}
 */
export const getDates = ({ orders: { dates } }) => ({ start: dates.start, end: dates.end })

export const getCcPwdTimeout = async ({ orders: { ccPwdTimeout }, ...state }) => {
	if (!ccPwdTimeout) {
		return false //no JWT for the password timeout, so there's nothing to validate :)
	}

	return checkJWT(state, ccPwdTimeout)
}

/*
 * Note: if you came here looking for getPumpkinTime, use PumpkinTimer.isset() instead - that's enough. If it's not, it
 *  means what you're coding probably belongs inside that class instead.
 */

//in normal cases this would also get wrapped in useObjSelector(), but it's used directly with the state, so... no need.
export const getOrderIds = ({ orders }) => orders.list ? orders.list.map(order => order.id).sort(): []

/**
 * Quicker and more precise than isEqual (not sure why it failed, but we only need to compare modified_at anyway, so...)
 * @param selector {function}
 * @see useObjSelector()
 * @return {function(state: object): Order[]}
 */
const useOrderListSelector = selector => useSelector(selector, (orders1, orders2) => {
	if (orders1.length != orders2.length) {
		return false //no need for further comparison, the lists are different :)
	}
	if (!orders1.length) {
		return true //if they're the same and the first list is empty, there's nothing to compare - they're both empty
	}

	//ok... same size and contain something; let's loop the first and make sure it has the same, updated orders
	/** @type Order */ let order1, order2
	for (let index in orders1) {
		order1 = orders1[index]
		order2 = orders2[index]
		if (!Order.isEqual(order1, order2)) {
			return false
		}
	}
	return true
})

const getOrders = status => ({ orders: { list, dates }, settings }) => {
	if (!list.length) {
		return []
	}
	//when enableCompleted is off, orders go to the Completed list after 1 hour of their due time
	const enableCompleted = TabletOptions.fromState({ settings }).enableCompleted
	const completed = order => enableCompleted? order.is_completed : order.diff_minutes < -60

	//Doing the date filtering here allows us to consistently show all future orders, regardless if the user selected a start date in the future, or an "early" end date (or is seeing Today only, etc).
	//TODO should all those boundaries be timezoned? or could we just strip all time comparisons?
	const endToday  = endOfToday()
	const startDate = parseISO(dates.start)
	const endDate   = endOfDay(parseISO(dates.end))

	const current = order => !isBefore(order.ready_at, startDate) && !isAfter(order.ready_at, endDate)
	const future  = order => isAfter(order.ready_at, endToday)

	/** @type {Order[]} */ const orders = Order.map(list)
	switch (status) { // eslint-disable-line default-case
		case Order.STATUS.ACTIVE:
			return orders.filter(order => current(order) && !order.is_cancelled && !completed(order))

		case Order.STATUS.COMPLETED:
			return orders.filter(order => (current(order) || future(order)) && completed(order))

		case Order.STATUS.CANCELED:
			return orders.filter(order => (current(order) || future(order)) && order.is_cancelled)

		case Order.STATUS.FUTURE:
			return orders.filter(order => future(order) && !order.is_cancelled)
	}
}

/**
 * @param status
 * @returns {Order[]}
 */
export const useOrders = status => useOrderListSelector(getOrders(status))

/**
 * Use with {@link useObjSelector()}
 * @param state
 * @returns {Object}
 */
export const getOrdersCount = state => mapValues(Order.STATUS, st => getOrders(st)(state).length)

/**
 * Finds an order given the ID, and returns it and a bool indicating if it's a
 * final object or if it needs further fetching.
 * @param id
 * @returns [{Order}, {boolean}]
 */
export const useOrder = id => useSelector(
	Order.fromState(id),
	/**
	 *
	 * @param orderA {Order}
	 * @param tmpA {boolean}
	 * @param orderB {Order}
	 * @param tmpB {boolean}
	 * @returns {boolean}
	 */
	([orderA, tmpA], [orderB, tmpB]) => (tmpA == tmpB && Order.isEqual(orderA, orderB))
)

/**
 * Whether the given ID is inside the loaded Orders List
 * @param id
 * @returns {function({orders: {list: object[]}}): boolean}
 */
export const hasOrder = id => ({ orders: { list } }) => list.some(order => order.id == id)

export const useApiSearch = () => useOrderListSelector(({ orders }) => Order.map(orders.search))

export const useLocalSearch = (query, period) => useOrderListSelector(({ orders: { list } }) => {
	if (!query) {
		return []
	}

	const limit = subDays(endOfToday(), period)
	return Order.map(list).filter(/** @type Order */ order => (
		//FIXME: this should be done with created_at, but that's not available at the API yet
		//FIXME: customer_current_phone is also not available
		isAfter(order.ready_at, limit) &&
		[String(order.id)/*, order.customer_current_phone*/, order.customer_name].some(v => v.indexOf(query) >= 0)
	))
})

export const useDeliveryOptions = () => useObjSelector(({ orders }) => orders.currentDeliveryOptions)
//endregion

export const {
	fetchOrders,
	updateDates,
	searchApiOrders,
	customOrdersSearch,
	fetchOrder,
	toggleCancelOrder,
	toggleCompleteOrder,
	resendOrder,
	grabPaymentInfo,
	notifyCustomer,
	rescheduleOrder,
	adjustOrder,
	removeAdjustment,
	estimateDelivery,
	assignDriver,
	cancelDriver,
	deleteDelivery,
	toggleVIP,
} = slice.thunks

export const { appendOrder, clearOrders, clearSearch, setPumpkinTime, loadDeliveryOptions } = slice.actions
export default slice.reducer
